/**
 * `reports` — the monthly document, section by section.
 *
 * `mockups/client/report-2026-08.html` is the specification: every `<section>` on
 * that page is one section kind here, and the shape of each is exactly what that
 * section prints and no more. `.docs/16-files-calls-reports.md`: "the shapes are the
 * August report's sections, no more".
 *
 * A section's `data` is validated on write **and** on read. On write because staff
 * and agents type it; on read because a section written against last quarter's shape
 * would otherwise render as a hole in a document a client is looking at.
 *
 * Two of the document's sections are **derived and never stored** — the social
 * calendar comes from session 12's content plan and deliverables, the widget counters
 * from session 13's summary — so neither has a stored shape here.
 *
 * Three rules run through the file.
 *
 *   **A figure the document computes is not stored.** A campaign's return on ad spend
 *   is revenue ÷ spend, a channel's engagement rate is engagements ÷ reach, and every
 *   `is-total` row in the mockup is a sum. Storing them would be storing an answer
 *   that can disagree with its own inputs.
 *
 *   **A figure the document *prints* and cannot recompute is stored.** `seo_content`'s
 *   `5 / 4` meter is `retainers.allowanceStatus` on the 28th, and the month runs to
 *   the 31st — an issued report that re-derived it would change after it was issued.
 *   `.docs/00-index.md` §6: report data is staff-entered.
 *
 *   **Every string is a string somebody wrote.** Nothing here formats a number and
 *   nothing here is prose this system composed: `answer`, `note` and `breakdown` are
 *   the sentences on the page, in the words the studio used.
 */
import { z } from 'zod';
import { AllowanceStatusSchema, ContentPlanItemStateSchema, ContentTypeCodeSchema, } from './retainers.js';
import { DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema } from './primitives.js';
import { ClientSlugSchema } from './refs.js';
import { OpenDecisionSchema } from './work-orders.js';
/* ------------------------------------------------------------------ the words */
/**
 * A report is drafted and then issued, and that is the whole of its life.
 *
 * **Not a `states.ts` machine, deliberately.** Two states and one transition — the
 * exhaustive table test `.docs/00-index.md` §4 asks of a machine would be three rows,
 * and `stateEnum` would be the only thing it bought. The single guard is
 * `modules/reports/service.ts` `assertDraft`, and the column is `text` with an
 * `inCheck` over this list. The session report says what would change that.
 */
export const REPORT_STATUSES = ['draft', 'issued'];
export const ReportStatusSchema = z
    .enum(REPORT_STATUSES)
    .describe('draft — in preparation; sections can still be written. issued — final on the issue day; ' +
    'nothing about it changes again.');
/**
 * The ten sections, in the order `report-2026-08.html` prints them.
 *
 * The order **is** `report_section.seq`: a document reads top to bottom and nothing
 * chooses that order twice.
 */
export const REPORT_SECTION_KINDS = [
    'overview',
    'seo',
    'seo_content',
    'social',
    'ads',
    'video',
    'odoo',
    'consulting',
    'widgets',
    'upcoming',
];
/** The kinds a `report_section` row exists for. The `CHECK` is over this list. */
export const STORED_SECTION_KINDS = REPORT_SECTION_KINDS.filter((kind) => kind !== 'widgets');
export const ReportSectionKindSchema = z
    .enum(REPORT_SECTION_KINDS)
    .describe('Which section of the document: overview, seo, seo_content, social, ads, video, odoo, ' +
    'consulting, widgets or upcoming. widgets is derived from the usage platform and is ' +
    'never written.');
/** Where a kind sits in the document. The index in `REPORT_SECTION_KINDS`. */
export function sectionSeq(kind) {
    return REPORT_SECTION_KINDS.indexOf(kind) + 1;
}
/* ---------------------------------------------------------------- the pieces */
/**
 * One `.stat` card: a label, the figure as it prints, the change beside it and the
 * line under it.
 *
 * `value` is a **string** because the mockup's own stats are `19 of 24` and
 * `92 / 100` as often as they are a number, and half of a "N of M" in a numeric field
 * is a stat that prints wrong in one section and right in another. The one place a
 * figure has to be a number is `overview`, because the dashboard computes a change
 * from it — and there it is one.
 */
export const ReportStatSchema = z
    .strictObject({
    label: z.string().min(1).max(120).describe('What it measures — "Keywords in the top 10".'),
    value: z.string().min(1).max(40).describe('The figure as it prints — "47", "19 of 24".'),
    delta: z
        .string()
        .max(60)
        .default('')
        .describe('The change beside it — "↑ 6 on July". Empty when there is none.'),
    note: z
        .string()
        .max(400)
        .default('')
        .describe('The line under it — "Of 64 tracked." Empty when there is none.'),
})
    .describe('One stat card in a report section.');
/**
 * One `row row--top` line: the shape the SEO fixes, the best social pieces and the
 * ad changes all print in.
 *
 * Three sections, one shape, and it is the contract's own `.row--top` with its
 * `.frow__ic` mark — extracting it is what stops the third copy drifting from the
 * first.
 */
export const ReportEntrySchema = z
    .strictObject({
    mark: z
        .string()
        .max(4)
        .default('')
        .describe('The mark in the icon column — "fix", "sch", "+", "−", "1". Empty for none.'),
    title: z.string().min(1).max(200).describe('What it was, in a line.'),
    note: z.string().max(600).default('').describe('The muted line under it. Empty for none.'),
    on: DateOnlySchema.nullable().default(null).describe('The day, or null when it has none.'),
    tag: z
        .string()
        .max(40)
        .default('')
        .describe('The label on the right — "Best reach", "September". Empty for none.'),
    needs_attention: z
        .boolean()
        .default(false)
        .describe('True when the next move is the client’s — the ochre pill on an Odoo health-check ' +
        'finding ("Your decision"). False for a line that is only being reported.'),
})
    .describe('One line of a report list: a mark, what happened, and when.');
/**
 * One `.meter`: what the period included and what it used.
 *
 * `used` is a number rather than an integer because Odoo management is sold by the
 * hour and the mockup prints `6.0 / 6`; `included` is whole because an allowance is
 * something somebody agreed to.
 */
export const ReportAllowanceSchema = z
    .strictObject({
    label: z.string().min(1).max(120).describe('What the allowance is for — "Articles written".'),
    used: z.number().min(0).max(1_000_000).describe('How many were delivered — 5, 106, 6.0.'),
    included: z.int().min(0).max(1_000_000).describe('How many the period includes — 4, 60, 6.'),
    fraction_digits: z
        .int()
        .min(0)
        .max(2)
        .default(0)
        .describe('Decimal places on what was used. One for an allowance sold by the hour, which is ' +
        'what makes report-2026-08.html print "6.0 / 6" beside "5 / 4"; none for a count.'),
    note: z
        .string()
        .max(400)
        .default('')
        .describe('The line under the bar — "The fifth is past the allowance."'),
})
    .describe('One allowance as the report prints it, on the day it was written.');
/** A label and a value — the `kv kv--bare` rows an Odoo health check prints. */
export const ReportFactSchema = z
    .strictObject({
    label: z.string().min(1).max(80).describe('What it is — "Last backup verified".'),
    value: z.string().min(1).max(200).describe('What it says — "19 Aug 2026".'),
})
    .describe('One fact in a list of facts.');
/* --------------------------------------------------------------- the overview */
/**
 * The seven figures the document leads with, and the four the dashboard prints.
 *
 * `.docs/00-index.md` §6: "The dashboard's four headline numbers come from the
 * report's `overview` section series." That is why this is the one section whose
 * figures are numbers rather than strings — a change against last month is arithmetic
 * and nothing else in the document does any.
 */
export const OVERVIEW_METRICS = [
    'organic_sessions',
    'wholesale_leads',
    'keywords_top10',
    'social_reach',
    'roas',
    'pieces_published',
    'video_edits',
];
export const OverviewMetricSchema = z
    .enum(OVERVIEW_METRICS)
    .describe('Which headline figure: organic_sessions, wholesale_leads, keywords_top10, social_reach, ' +
    'roas, pieces_published or video_edits.');
export const OVERVIEW_METRIC_SPECS = [
    {
        metric: 'organic_sessions',
        label: 'Organic sessions',
        short_label: 'Organic sessions',
        table_label: 'Sessions',
        delta: 'percent',
        headline: 1,
        fraction_digits: 0,
        suffix: '',
    },
    {
        metric: 'wholesale_leads',
        label: 'Wholesale leads',
        short_label: 'Wholesale leads',
        table_label: 'Leads',
        delta: 'absolute',
        headline: 2,
        fraction_digits: 0,
        suffix: '',
    },
    {
        metric: 'keywords_top10',
        label: 'Keywords in the top 10',
        short_label: 'Keywords in top 10',
        table_label: 'In the top 10',
        delta: 'absolute',
        headline: 3,
        fraction_digits: 0,
        suffix: '',
    },
    {
        metric: 'social_reach',
        label: 'Social reach',
        short_label: 'Social reach',
        table_label: 'Reach',
        delta: 'percent',
        headline: 4,
        fraction_digits: 0,
        suffix: '',
    },
    {
        metric: 'roas',
        label: 'Return on ad spend',
        short_label: 'Return on ad spend',
        table_label: 'Return',
        delta: 'absolute',
        headline: null,
        fraction_digits: 2,
        suffix: '×',
    },
    {
        metric: 'pieces_published',
        label: 'Pieces published',
        short_label: 'Pieces published',
        table_label: 'Pieces',
        delta: 'absolute',
        headline: null,
        fraction_digits: 0,
        suffix: '',
    },
    {
        metric: 'video_edits',
        label: 'Video edits delivered',
        short_label: 'Video edits',
        table_label: 'Edits',
        delta: 'absolute',
        headline: null,
        fraction_digits: 0,
        suffix: '',
    },
];
/** One metric's spec. The only way to get one. */
export function overviewMetricSpec(metric) {
    const found = OVERVIEW_METRIC_SPECS.find((spec) => spec.metric === metric);
    if (found === undefined) {
        // Unreachable while the list above covers the union; the compiler cannot see it.
        throw new Error(`No overview metric spec for "${metric}".`);
    }
    return found;
}
/** The four the dashboard prints, in its own order. */
export const HEADLINE_METRICS = OVERVIEW_METRIC_SPECS.filter((spec) => spec.headline !== null)
    .slice()
    .sort((a, b) => (a.headline ?? 0) - (b.headline ?? 0))
    .map((spec) => spec.metric);
export const OverviewScalarPointSchema = z
    .strictObject({
    month: PeriodSchema.describe('The month this reading is for, YYYY-MM.'),
    value: z.number().describe('The reading — 14280 sessions, 63 leads, 5.67 for a ratio.'),
    baseline: z
        .boolean()
        .default(false)
        .describe('True for a month before the studio started: shown, but nothing is claimed for it.'),
})
    .describe('One month of a scalar figure. It has a value and no denominator.');
export const OverviewKeywordsPointSchema = z
    .strictObject({
    month: PeriodSchema.describe('The month this reading is for, YYYY-MM.'),
    in: z.int().min(0).describe('How many tracked keywords were in the top 10.'),
    tracked: z.int().min(0).describe('How many keywords were tracked in all.'),
    baseline: z
        .boolean()
        .default(false)
        .describe('True for a month before the studio started: shown, but nothing is claimed for it.'),
})
    .describe('One month of the keywords figure: how many were in the top 10, out of tracked.')
    .superRefine((point, ctx) => {
    if (point.in > point.tracked) {
        ctx.addIssue({
            code: 'custom',
            message: 'Keywords in the top 10 cannot exceed the number tracked.',
            path: ['in'],
        });
    }
});
export const OverviewPointSchema = z.union([OverviewScalarPointSchema, OverviewKeywordsPointSchema]);
const OverviewSeriesFields = {
    since: PeriodSchema.describe('The first month this figure was reported. Ads reporting starts in June, so roas has a ' +
        'later one than the rest.'),
    note: z
        .string()
        .max(400)
        .default('')
        .describe('The line under the figure — "enquiries, not clicks". Empty for none.'),
    breakdown: z
        .string()
        .max(400)
        .default('')
        .describe('The detail the dashboard’s "Breakdown" fold prints — "41 from ads, 22 organic". ' +
        'Empty when the figure has none.'),
};
function scalarOverviewSeries(metric) {
    return z.strictObject({
        metric: z.literal(metric).describe('Which scalar overview metric this series carries.'),
        ...OverviewSeriesFields,
        series: z
            .array(OverviewScalarPointSchema)
            .min(1)
            .max(120)
            .describe('One scalar reading per month, oldest first. A missing month is left out.'),
    });
}
export const OverviewSeriesSchema = z
    .discriminatedUnion('metric', [
    scalarOverviewSeries('organic_sessions'),
    scalarOverviewSeries('wholesale_leads'),
    z.strictObject({
        metric: z.literal('keywords_top10').describe('The keywords-in-the-top-10 series.'),
        ...OverviewSeriesFields,
        series: z
            .array(OverviewKeywordsPointSchema)
            .min(1)
            .max(120)
            .describe('One in/tracked reading per month, oldest first. A missing month is left out.'),
    }),
    scalarOverviewSeries('social_reach'),
    scalarOverviewSeries('roas'),
    scalarOverviewSeries('pieces_published'),
    scalarOverviewSeries('video_edits'),
])
    .describe('One headline figure, month by month. Its metric discriminates its point shape.');
/** The numerator a chart plots, without weakening the schema back to value/total. */
export function overviewPointValue(point) {
    return 'value' in point ? point.value : point.in;
}
/** The one denominator in the overview, and null for every scalar metric. */
export function overviewPointTotal(point) {
    return 'tracked' in point ? point.tracked : null;
}
/* ---------------------------------------------------- how a figure moved */
/** One decimal on a percentage, which is what every delta in the mockups prints. */
export const OVERVIEW_DELTA_PERCENT_DIGITS = 1;
/**
 * How a figure moved against the month before it, the way its own spec reads it.
 *
 * `OverviewMetricSpec.delta` has said `'percent' | 'absolute'` since session 16 and
 * this is the arithmetic behind it: `↑ 18.0% on July` for organic sessions, `↑ 11 on
 * July` for wholesale leads. **The value only** — the string is the caller's, because
 * the API formats through `formatNumber` and the SPA through `useFormat()`, and a
 * locale in `packages/shared` would be a second answer to a question `useFormat` owns.
 *
 * A percentage against a previous reading of **zero** is not a percentage — it is a
 * division by nothing — so it answers null rather than Infinity. A count's change is a
 * subtraction and always exists.
 *
 * One copy, two callers: `readmodels/client-metrics.ts` builds the dashboard's tiles
 * and chart from it, and the report document's own overview chart prints the same
 * Change column beside the same figures. Two spellings of "+18.0%" that could disagree
 * is exactly the thing the DRY registry exists to stop.
 */
export function overviewDelta(kind, previous, current) {
    if (kind === 'absolute')
        return current - previous;
    if (previous === 0)
        return null;
    return ((current - previous) / previous) * 100;
}
/** `+5.9%`, `−2`. A change with no sign reads as a value rather than a movement. */
export function signedChange(display, value) {
    return value > 0 ? `+${display}` : display;
}
export const OverviewSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The month in a sentence — the `.answer` the document opens with.'),
    metrics: z
        .array(OverviewSeriesSchema)
        .max(OVERVIEW_METRICS.length)
        .default([])
        .describe('One entry per figure, at most one each. Empty while the section is being written.'),
})
    .describe('The month in figures, and the series behind each of them.')
    .superRefine((section, ctx) => {
    const seen = new Set();
    section.metrics.forEach((entry, index) => {
        if (seen.has(entry.metric)) {
            ctx.addIssue({
                code: 'custom',
                message: `The figure "${entry.metric}" appears twice.`,
                path: ['metrics', index, 'metric'],
            });
        }
        seen.add(entry.metric);
        let previous = null;
        entry.series.forEach((point, at) => {
            if (previous !== null && point.month <= previous) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'A series runs oldest first and carries each month once.',
                    path: ['metrics', index, 'series', at, 'month'],
                });
            }
            if (point.month < entry.since) {
                ctx.addIssue({
                    code: 'custom',
                    message: `A reading before "since" (${entry.since}) is a month nobody reported.`,
                    path: ['metrics', index, 'series', at, 'month'],
                });
            }
            previous = point.month;
        });
    });
});
/* -------------------------------------------------------------- SEO and GEO */
export const SeoKeywordSchema = z
    .strictObject({
    keyword: z.string().min(1).max(120).describe('The term — "peshtemal towel".'),
    volume: z.int().min(0).max(100_000_000).describe('Searches a month.'),
    position: z.int().min(1).max(1_000).describe('Where the site ranks for it today.'),
    change: z
        .string()
        .max(40)
        .default('')
        .describe('The 30-day move — "↑ 1", "↓ 2", "new entry". Empty when it did not move.'),
    landing_page: z.string().max(300).default('').describe('The page that ranks — "/wholesale".'),
    intent: z
        .string()
        .max(40)
        .default('')
        .describe('What the searcher wants — "Commercial", "Transactional", "Informational".'),
    needs_attention: z
        .boolean()
        .default(false)
        .describe('True for a term that slipped: the ochre row on the page.'),
})
    .describe('One tracked keyword and where it stands.');
export const SeoAssistantsSchema = z
    .strictObject({
    week_of: DateOnlySchema.describe('The Monday of the week these were asked.'),
    prompts: z.int().min(1).max(1_000).describe('How many buying questions each was asked — 24.'),
    rows: z
        .array(z
        .strictObject({
        assistant: z.string().min(1).max(80).describe('Which assistant — "Perplexity".'),
        cited: z
            .int()
            .min(0)
            .max(1_000)
            .describe('How many of the prompts named or cited them.'),
    })
        .describe('One assistant’s week.'))
        .max(20)
        .describe('One row per assistant, in the order they print.'),
    most_cited_page: z
        .string()
        .max(300)
        .default('')
        .describe('The page assistants cite most — "/blog/turkish-cotton-vs-blend".'),
})
    .describe('Which assistants cite the client, and how it was asked.');
export const SeoSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    stats: z.array(ReportStatSchema).max(6).default([]).describe('The stat cards, in order.'),
    keywords: z
        .array(SeoKeywordSchema)
        .max(200)
        .default([])
        .describe('The tracked set, in the order it prints.'),
    keywords_note: z
        .string()
        .max(800)
        .default('')
        .describe('The paragraph under the keyword table. Empty for none.'),
    assistants: SeoAssistantsSchema.nullable()
        .default(null)
        .describe('The GEO block, or null in a month it was not measured.'),
    fixes: z
        .array(ReportEntrySchema)
        .max(50)
        .default([])
        .describe('The technical and on-page fixes that shipped.'),
})
    .describe('SEO and GEO: rankings, assistant visibility, technical health and what was fixed.');
/* ------------------------------------------------------------- SEO content */
export const SeoArticleSchema = z
    .strictObject({
    title: z.string().min(1).max(300).describe('The headline as published.'),
    target_keyword: z.string().max(120).default('').describe('The term it was written for.'),
    words: z.int().min(0).max(1_000_000).describe('How long it is.'),
    published_on: DateOnlySchema.nullable()
        .default(null)
        .describe('The day it went live, or null.'),
    over_allowance: z
        .boolean()
        .default(false)
        .describe('True when it sat past the allowance and was billed as an extra.'),
    note: z.string().max(300).default('').describe('The muted line under the title.'),
})
    .describe('One article written this month.');
export const SeoContentSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    allowance: ReportAllowanceSchema.nullable()
        .default(null)
        .describe('The articles meter, as it stood when the report was written.'),
    published_since: ReportStatSchema.nullable()
        .default(null)
        .describe('The "Published since March" card, or null.'),
    articles: z
        .array(SeoArticleSchema)
        .max(100)
        .default([])
        .describe('The articles, oldest first.'),
    note: z
        .string()
        .max(800)
        .default('')
        .describe('The paragraph under the table — what a brief carries. Empty for none.'),
})
    .describe('The month’s articles, against the allowance that covered them.');
/* ------------------------------------------------------------------ social */
export const SocialChannelSchema = z
    .strictObject({
    channel: z.string().min(1).max(60).describe('Where — "Instagram", "TikTok", "Pinterest".'),
    reach: z.int().min(0).max(1_000_000_000).describe('Accounts reached.'),
    posts: z.int().min(0).max(100_000).describe('How many posts ran there.'),
    engagements: z.int().min(0).max(1_000_000_000).describe('Likes, saves, comments and shares.'),
})
    .describe('One channel’s month. The engagement rate is engagements ÷ reach and is not stored — the ' +
    'total row on the page is a sum of these.');
export const SocialCreditRowSchema = z
    .strictObject({
    content_type: ContentTypeCodeSchema,
    unit_credits: z
        .int()
        .min(0)
        .max(1_000)
        .describe('What one piece of this type costs — 5, 3, 2, 1.'),
    pieces: z.int().min(0).max(10_000).describe('How many ran.'),
    credits_spent: z.int().min(0).max(1_000_000).describe('pieces × unit_credits.'),
    credits_in_allowance: z
        .int()
        .min(0)
        .max(1_000_000)
        .describe('How many of those credits the allowance covered.'),
})
    .describe('One content type’s share of the month’s credits.');
export const SocialSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    stats: z.array(ReportStatSchema).max(6).default([]).describe('The stat cards, in order.'),
    channels: z
        .array(SocialChannelSchema)
        .max(20)
        .default([])
        .describe('Reach by channel, in the order it prints.'),
    channels_note: z
        .string()
        .max(400)
        .default('')
        .describe('The caption under the reach chart. Empty for none.'),
    allowances: z
        .array(ReportAllowanceSchema)
        .max(6)
        .default([])
        .describe('The credits meter and the channels meter, as they stood.'),
    best_pieces: z
        .array(ReportEntrySchema)
        .max(20)
        .default([])
        .describe('The pieces that performed best, and the one that did not.'),
    credits: z
        .array(SocialCreditRowSchema)
        .max(20)
        .default([])
        .describe('Where the credits went, by content type.'),
    credits_note: z
        .string()
        .max(800)
        .default('')
        .describe('The paragraph under the credits table. Empty for none.'),
})
    .describe('Social: reach, engagement, where the credits went. The calendar is not here — it is ' +
    'derived from the content plan and the deliverables on every read.');
/* ---------------------------------------------------- the derived calendar */
export const SocialCalendarItemSchema = z
    .strictObject({
    on: DateOnlySchema.describe('The day it runs.'),
    content_type: ContentTypeCodeSchema,
    channels: z.array(z.string()).describe('Where it runs — "instagram", "tiktok", "pinterest".'),
    title: z.string().describe('What it is.'),
    state: ContentPlanItemStateSchema,
    pieces: z
        .int()
        .min(1)
        .default(1)
        .describe('How many pieces this entry stands for. Always 1 from a content plan, where an item ' +
        'is one piece on one day. More than 1 when it comes from a deliverable that recorded ' +
        'several at once — work past an allowance is grouped by unit so it is one extra and ' +
        'one invoice line, and a calendar that drew that as eight separate cards on one day ' +
        'would be asserting eight publications nobody wrote down.'),
})
    .describe('One entry on the calendar, from the content plan or from what was delivered.');
export const SocialCalendarSchema = z
    .strictObject({
    period: PeriodSchema.describe('The month it covers.'),
    as_at: Rfc3339Schema.describe('The instant the calendar was read at — the **document’s** own, not the reader’s. Now ' +
        'for a report still in preparation (never past the end of the month it is about), and ' +
        'the issue instant for one that has been issued, so an issued report shows the same ' +
        'calendar whatever day it is opened on.'),
    plan_ref: z
        .string()
        .nullable()
        .describe('The content plan behind it, PLAN-YYYY-MM, or null when there is none.'),
    published: z.int().describe('Pieces that went out. A count of pieces, not of entries.'),
    scheduled: z.int().describe('Pieces queued with the platform.'),
    needs_signoff: z.int().describe('Pieces waiting on the client — the "needs you" days.'),
    planned: z.int().describe('Pieces on the calendar and not yet queued.'),
    items: z
        .array(SocialCalendarItemSchema)
        .describe('Every entry, by day then by plan order. One entry can stand for several pieces.'),
})
    .describe('The month’s calendar as the document reads it. Derived, never stored — and derived at the ' +
    'document’s own as-of instant, so an issued report’s does not change.');
/* --------------------------------------------------------------------- ads */
export const AdsCampaignSchema = z
    .strictObject({
    name: z.string().min(1).max(120).describe('The campaign — "Shopping — Towels".'),
    note: z.string().max(300).default('').describe('The muted line beside it.'),
    spend: MoneySchema.describe('What was paid to the platform, on the client’s own card.'),
    conversions: z.int().min(0).max(10_000_000).describe('How many converted.'),
    revenue: MoneySchema.describe('Tracked revenue from it.'),
    status: z
        .string()
        .max(40)
        .default('')
        .describe('Where it stands — "Scaling", "Holding", "Learning", "Under review".'),
    needs_attention: z
        .boolean()
        .default(false)
        .describe('True for a campaign under review: the ochre row.'),
})
    .describe('One campaign. Its return on ad spend is revenue ÷ spend and is not stored, and neither ' +
    'is the total row.');
export const AdsSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    figures_to: DateOnlySchema.nullable()
        .default(null)
        .describe('The day the platform was last reconciled — the section head’s "to 24 August".'),
    target_roas: z
        .number()
        .min(0)
        .max(1_000)
        .nullable()
        .default(null)
        .describe('The return the client needs for ads to pay for themselves — the chart’s threshold ' +
        'line. Null when there is no agreed target, and then no line is drawn.'),
    stats: z.array(ReportStatSchema).max(6).default([]).describe('The stat cards, in order.'),
    campaigns: z.array(AdsCampaignSchema).max(50).default([]).describe('The campaigns, in order.'),
    spend_band: z
        .strictObject({
        spent: MoneySchema.describe('Spend so far this month.'),
        band: MoneySchema.describe('The band the management block covers.'),
        note: z.string().max(400).default('').describe('The line under the bar.'),
    })
        .nullable()
        .default(null)
        .describe('The spend band meter, or null when the block does not carry one.'),
    decision: z
        .strictObject({
        heading: z
            .string()
            .min(1)
            .max(200)
            .describe('The bold line — "On pace to breach the band."'),
        body: z.string().min(1).max(1_200).describe('What the client has to decide, and by when.'),
    })
        .nullable()
        .default(null)
        .describe('The one decision this month leaves open, or null when it leaves none.'),
    changes: z
        .array(ReportEntrySchema)
        .max(50)
        .default([])
        .describe('The changes that moved the numbers, oldest first.'),
})
    .describe('Ads: what ran, what it returned, and what it leaves for the client to decide.');
/* ------------------------------------------------------------------- video */
export const VideoEditSchema = z
    .strictObject({
    title: z.string().min(1).max(300).describe('What the cut is — "Mill to bathroom in 6 steps".'),
    ref: z.string().max(40).default('').describe('The studio’s own identifier — "V-2609".'),
    length: z
        .string()
        .max(10)
        .default('')
        .describe('How long it runs, as it reads — "0:28". A label, not a quantity.'),
    delivered_on: DateOnlySchema.nullable().default(null).describe('The day it landed, or null.'),
    over_allowance: z.boolean().default(false).describe('True when it sat past the allowance.'),
    note: z.string().max(400).default('').describe('The muted line under the title.'),
})
    .describe('One edit delivered this month.');
export const VideoSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    stats: z.array(ReportStatSchema).max(6).default([]).describe('The stat cards, in order.'),
    allowance: ReportAllowanceSchema.nullable()
        .default(null)
        .describe('The edits meter, as it stood when the report was written.'),
    edits: z.array(VideoEditSchema).max(100).default([]).describe('The edits, oldest first.'),
    note: z
        .string()
        .max(800)
        .default('')
        .describe('The paragraph under the table. Empty for none.'),
})
    .describe('Video: what was cut, what it reached, and against which allowance.');
/* -------------------------------------------------------------------- Odoo */
export const OdooTicketSchema = z
    .strictObject({
    ref: z.string().max(40).default('').describe('The ticket — "ODO-243".'),
    subject: z.string().min(1).max(300).describe('What was asked.'),
    short_label: z
        .string()
        .max(60)
        .default('')
        .describe('The short name the hours chart uses — "Reorder rule fix".'),
    note: z.string().max(600).default('').describe('What was done, in a line.'),
    priority: z
        .string()
        .max(20)
        .default('')
        .describe('How urgent — "Normal", "High". Empty when it does not matter.'),
    opened_on: DateOnlySchema.nullable().default(null).describe('The day it was raised, or null.'),
    first_response: z
        .string()
        .max(20)
        .default('')
        .describe('How long the first answer took, as it reads — "4 h".'),
    hours: z.number().min(0).max(1_000).describe('Hours logged against the block.'),
    state: z
        .string()
        .max(40)
        .default('')
        .describe('Where it stands — "Resolved", "Blocked on you".'),
    needs_attention: z
        .boolean()
        .default(false)
        .describe('True when the next move is the client’s.'),
})
    .describe('One support ticket. The hours chart is these rows, longest first.');
export const OdooHealthCheckSchema = z
    .strictObject({
    run_on: DateOnlySchema.describe('The day the check ran.'),
    findings: z
        .array(ReportEntrySchema)
        .max(20)
        .default([])
        .describe('What it found, and whose move each one is.'),
    note: z
        .string()
        .max(400)
        .default('')
        .describe('The line under the findings — "Everything else was clear."'),
    facts: z
        .array(ReportFactSchema)
        .max(20)
        .default([])
        .describe('The estate: hosting, users, apps, size, version.'),
    upgrade_note: z
        .string()
        .max(800)
        .default('')
        .describe('What the next version costs and when. Empty when there is nothing due.'),
})
    .describe('The monthly health check, which is inside the block and charges no hours.');
export const OdooSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    allowance: ReportAllowanceSchema.nullable()
        .default(null)
        .describe('The hours meter, as it stood when the report was written.'),
    stats: z.array(ReportStatSchema).max(6).default([]).describe('The stat cards, in order.'),
    tickets: z.array(OdooTicketSchema).max(100).default([]).describe('The tickets, by reference.'),
    health_check: OdooHealthCheckSchema.nullable()
        .default(null)
        .describe('The month’s health check, or null when none ran.'),
})
    .describe('Odoo: where the block’s hours went, and what the health check found.');
/* -------------------------------------------------------------- consulting */
export const ConsultingSessionSchema = z
    .strictObject({
    on: DateOnlySchema.describe('The day it happened.'),
    title: z.string().min(1).max(300).describe('What it was about.'),
    hours: z.number().min(0).max(100).describe('How long it ran.'),
    summary: z.string().max(2_000).default('').describe('What was decided, in a paragraph.'),
})
    .describe('One consulting session. Every hour is quoted and approved before it starts.');
export const ConsultingSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('The section in a sentence. Blank while it is being written; issuing needs one.'),
    sessions: z
        .array(ConsultingSessionSchema)
        .max(20)
        .default([])
        .describe('The month’s sessions, oldest first.'),
    done_since: z
        .string()
        .max(800)
        .default('')
        .describe('What shipped out of the sessions — the "Done since" line. Empty for none.'),
    open_items_note: z
        .string()
        .max(600)
        .default('')
        .describe('The sentence above the open items. Empty for none.'),
    open_items: z
        .array(z
        .strictObject({
        title: z.string().min(1).max(300).describe('What is still with the client.'),
        note: z.string().max(400).default('').describe('Since when, and what it blocks.'),
    })
        .describe('One thing a session left open.'))
        .max(30)
        .default([])
        .describe('What the sessions left open. None of it is billable.'),
})
    .describe('Consulting: the sessions, what came out of them, and what is still open.');
/* ----------------------------------------------------------------- widgets */
/**
 * Derived, and stored nowhere: `.docs/16-files-calls-reports.md` — "`widgets`
 * (derived from 13's summary — not stored)".
 */
export const ReportWidgetSchema = z
    .strictObject({
    widget: z.string().describe('The widget, by reference — wgt_chat_9f3a.'),
    name: z.string().describe('What the client calls it — "Support chatbot".'),
    site: z.string().describe('Where it runs — "casaturca.com/wholesale".'),
    unit_noun: z.string().describe('One billable event, in the client’s words — "conversation".'),
    unit_noun_plural: z.string().describe('More than one — "conversations".'),
    used: z.int().describe('Units recorded this period.'),
    live_since: DateOnlySchema.describe('The day it started serving.'),
    usage_to: DateOnlySchema.describe('The last day included in the counter.'),
    narrative: z
        .string()
        .describe('The quantitative line under the counter: site, live date and the derived daily range.'),
    per_day: z
        .object({
        min: z.number().nullable().describe('The quietest day’s count, or null when none.'),
        max: z.number().nullable().describe('The busiest day’s count, or null when none.'),
        average: z
            .number()
            .nullable()
            .describe('The mean over the days recorded, unrounded. Null when there are none.'),
    })
        .describe('What a day looked like — report-2026-08.html §9: "Between 38 and 69 a day, ' +
        'averaging 53". Derived from the same daily series the widget’s own page charts.'),
})
    .describe('One widget’s month, read from the usage platform.');
export const WidgetsSectionSchema = z
    .strictObject({
    period: PeriodSchema.describe('The month these counters cover.'),
    as_at: Rfc3339Schema.describe('When they were read.'),
    period_note: z.string().describe('The short line beside the section heading.'),
    answer: z.string().describe('The section’s derived summary sentence.'),
    widgets: z
        .array(ReportWidgetSchema)
        .describe('One entry per widget, in the order they went live.'),
})
    .describe('The widgets that ran this month. Derived from the usage platform on every read.');
/* ---------------------------------------------------------------- upcoming */
export const UpcomingItemSchema = z
    .strictObject({
    when: z
        .string()
        .min(1)
        .max(60)
        .describe('When it happens, as it reads — "Tue 1 Sep", "wk 2 Sep", "Mid Sep".'),
    title: z.string().min(1).max(200).describe('What happens.'),
    note: z.string().max(600).default('').describe('The line under it. Empty for none.'),
    fold: z
        .boolean()
        .default(false)
        .describe('True for an item behind the "the rest of the month" fold rather than in the list.'),
})
    .describe('One thing on next month’s timeline.');
export const UpcomingSectionSchema = z
    .strictObject({
    answer: z
        .string()
        .max(800)
        .default('')
        .describe('Next month in a sentence. Blank while it is being written; issuing needs one.'),
    timeline: z
        .array(UpcomingItemSchema)
        .max(50)
        .default([])
        .describe('What is coming, in the order it happens.'),
    also_waiting: z
        .string()
        .max(800)
        .default('')
        .describe('What else is with the client, in a line. Empty for none.'),
})
    .describe('Next month, as the studio plans it. The open decisions are not here — they are derived ' +
    'from what is actually waiting on the client at read time.');
/* ------------------------------------------------------------- the document */
/**
 * The spend-band decision belongs to a report, not to work orders.
 *
 * `work-orders.ts` owns the catalogue-wide decision enum and reports must not widen it
 * with a local document concern. The upcoming section still needs a typed branch for
 * the notice it visibly repeats from Ads, so the report view uses this local union.
 */
export const ReportAdSpendBandDecisionSchema = z
    .strictObject({
    kind: z.literal('ad_spend_band'),
    period: PeriodSchema.describe('The report month whose ads section raised it.'),
    heading: z.string().min(1).max(200).describe('The bold line on the decision notice.'),
    body: z.string().min(1).max(1_200).describe('What the client has to decide.'),
})
    .describe('The ads spend-band decision repeated in Upcoming at render time.');
export const ReportOpenDecisionSchema = z.union([
    OpenDecisionSchema,
    ReportAdSpendBandDecisionSchema,
]);
export const SocialSectionViewSchema = SocialSectionSchema.extend({
    calendar: SocialCalendarSchema.nullable().describe('The month’s calendar as it stands now, or null when nothing was planned or delivered.'),
}).describe('The social section as the document prints it: what was stored, plus the calendar.');
export const UpcomingSectionViewSchema = UpcomingSectionSchema.extend({
    open_decisions: z
        .array(ReportOpenDecisionSchema)
        .describe('What is waiting on the client right now. Derived, never stored.'),
}).describe('The upcoming section as the document prints it: the timeline, plus what is open.');
/** One section of the document. `data` is whatever that kind's shape is. */
export const ReportSectionViewSchema = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('overview').describe('The month in figures.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: OverviewSectionSchema,
    }),
    z.object({
        kind: z.literal('seo').describe('SEO and GEO.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: SeoSectionSchema,
    }),
    z.object({
        kind: z.literal('seo_content').describe('The month’s articles.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: SeoContentSectionSchema,
    }),
    z.object({
        kind: z.literal('social').describe('Social, with its derived calendar.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: SocialSectionViewSchema,
    }),
    z.object({
        kind: z.literal('ads').describe('Ads.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: AdsSectionSchema,
    }),
    z.object({
        kind: z.literal('video').describe('Video.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: VideoSectionSchema,
    }),
    z.object({
        kind: z.literal('odoo').describe('Odoo.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: OdooSectionSchema,
    }),
    z.object({
        kind: z.literal('consulting').describe('Consulting.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: ConsultingSectionSchema,
    }),
    z.object({
        kind: z.literal('widgets').describe('The widgets, derived from the usage platform.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: WidgetsSectionSchema,
    }),
    z.object({
        kind: z.literal('upcoming').describe('Next month, with what is waiting on the client.'),
        seq: z.int().describe('Where it sits in the document.'),
        updated_at: Rfc3339Schema.nullable().describe('When it was last written, or null if derived.'),
        data: UpcomingSectionViewSchema,
    }),
])
    .describe('One section of a report: which kind it is, where it sits, and what it says.');
export const ReportDocumentSchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema.describe('The month it covers, YYYY-MM.'),
    period_label: z.string().describe('That month as it reads — "August 2026".'),
    status: ReportStatusSchema,
    headline: z.string().describe('The archive’s one-line summary of the month.'),
    issued_at: Rfc3339Schema.nullable().describe('When it stopped changing, or null while in draft.'),
    pdf_file_id: z.string().nullable().describe('The rendered PDF, by file id, or null.'),
    sections: z
        .array(ReportSectionViewSchema)
        .describe('The sections, in document order. A month with no ads block has no ads section.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('One month’s report: the stored sections, merged with the ones that are derived.');
/* --------------------------------------------------------------- the archive */
export const ReportListItemSchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema.describe('The month it covers, YYYY-MM.'),
    period_label: z.string().describe('That month as it reads — "August 2026".'),
    status: ReportStatusSchema,
    headline: z.string().describe('The one-line summary the archive prints.'),
    issued_at: Rfc3339Schema.nullable().describe('When it was issued, or null while in preparation.'),
    pdf_file_id: z
        .string()
        .nullable()
        .describe('The PDF, by file id, or null before it is rendered.'),
    sections: z.int().describe('How many sections it carries.'),
})
    .describe('One row of the report archive.');
export const ReportArchiveSchema = z
    .object({
    client: ClientSlugSchema,
    count: z.int().describe('How many reports there are — the archive’s hero figure.'),
    issue_day: z.int().describe('The day of the month a report is issued on. 28 by default.'),
    next_issue_on: DateOnlySchema.describe('The day the report being prepared is due.'),
    reports: z.array(ReportListItemSchema).describe('Newest month first.'),
})
    .describe('Every report a client has, newest first.');
/* ------------------------------------------------------------- what to write */
/** One section as a caller writes it: the kind decides the shape of `data`. */
export const ReportSectionInputSchema = z
    .discriminatedUnion('kind', [
    z
        .strictObject({
        kind: z.literal('overview').describe('The month in figures.'),
        data: OverviewSectionSchema,
    })
        .describe('The overview section.'),
    z
        .strictObject({ kind: z.literal('seo').describe('SEO and GEO.'), data: SeoSectionSchema })
        .describe('The SEO section.'),
    z
        .strictObject({
        kind: z.literal('seo_content').describe('The month’s articles.'),
        data: SeoContentSectionSchema,
    })
        .describe('The SEO content section.'),
    z
        .strictObject({ kind: z.literal('social').describe('Social.'), data: SocialSectionSchema })
        .describe('The social section. Its calendar is derived and is not written here.'),
    z
        .strictObject({ kind: z.literal('ads').describe('Ads.'), data: AdsSectionSchema })
        .describe('The ads section.'),
    z
        .strictObject({ kind: z.literal('video').describe('Video.'), data: VideoSectionSchema })
        .describe('The video section.'),
    z
        .strictObject({ kind: z.literal('odoo').describe('Odoo.'), data: OdooSectionSchema })
        .describe('The Odoo section.'),
    z
        .strictObject({
        kind: z.literal('consulting').describe('Consulting.'),
        data: ConsultingSectionSchema,
    })
        .describe('The consulting section.'),
    z
        .strictObject({
        kind: z.literal('upcoming').describe('Next month.'),
        data: UpcomingSectionSchema,
    })
        .describe('The upcoming section. Its open decisions are derived and are not written here.'),
])
    .describe('A section to write. "kind" decides which shape "data" takes; there is no branch for ' +
    'widgets, which is derived from the usage platform on every read.');
/* ------------------------------------------------------ what an agent can read */
export const ReportDataDeliverableSchema = z
    .object({
    service: z.string().describe('The block it was delivered against, by rate-card code.'),
    content_type: ContentTypeCodeSchema.nullable().describe('For social content, which kind of piece.'),
    qty: z.number().describe('How many — 1 article, 2 reels, 1.5 hours.'),
    credits: z.int().nullable().describe('What it cost against a credit allowance, or null.'),
    title: z.string().nullable().describe('What it was, in a line.'),
    refs: z.array(z.string()).describe('The studio’s own identifiers — "V-2609", "ODO-243".'),
    channels: z.array(z.string()).describe('Where a piece ran.'),
    delivered_on: DateOnlySchema.describe('The day it landed.'),
    over: z.number().describe('How much of it was past the allowance when it was recorded.'),
    extra_ref: z.string().nullable().describe('The approved extra covering the overage, or null.'),
})
    .describe('One deliverable, as the report would print it.');
export const ReportDataSchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema.describe('The month, YYYY-MM.'),
    kind: ReportSectionKindSchema,
    note: z
        .string()
        .describe('What this system knows about this section and what it does not. An ads section ' +
        'cannot be filled from here at all: the spend is on the client’s own Google account.'),
    allowances: z
        .array(AllowanceStatusSchema)
        .describe('The allowances this section covers, for the period. Empty when it covers none.'),
    deliverables: z
        .array(ReportDataDeliverableSchema)
        .describe('What was delivered against them, oldest first. Empty when the section has none.'),
    calendar: SocialCalendarSchema.nullable().describe('The content calendar, for the social section. Null for every other.'),
    widgets: z
        .array(ReportWidgetSchema)
        .describe('The widgets that ran, for the widgets section. Empty for every other.'),
    open_decisions: z
        .array(ReportOpenDecisionSchema)
        .describe('What is waiting on the client, for the upcoming section. Empty for every other.'),
    previous_period: PeriodSchema.nullable().describe('The month before, whose report carries the series to copy forward. Null when there is none.'),
})
    .describe('What this system already knows about one section of one month.');
/* ------------------------------------------------------- the dashboard metrics */
export const ClientMetricDeltaSchema = z
    .object({
    kind: z
        .enum(['percent', 'absolute'])
        .describe('percent — the change reads as a percentage. absolute — it reads as a count.'),
    value: z
        .number()
        .describe('The change itself: a percentage for percent, a difference for absolute.'),
    display: z.string().describe('The change as it prints — "18.0%", "11".'),
})
    .describe('How a figure moved against the reading before it.');
export const ClientMetricReadingSchema = z
    .object({
    month: PeriodSchema.describe('The month, YYYY-MM.'),
    value: z.number().describe('The reading.'),
})
    .describe('One reading, for a page that has to name the month it came from.');
export const ClientMetricChartSchema = z
    .object({
    conclusion: z
        .string()
        .describe('What the line says, in a sentence. The chart’s aria-label and its conclusion.'),
    table: z
        .object({
        columns: z
            .array(z.object({
            label: z.string().describe('The column header.'),
            right: z.boolean().describe('True for a column of figures.'),
        }))
            .describe('The table view’s columns.'),
        rows: z
            .array(z.array(z.string()))
            .describe('One array per month, already formatted — "Jan 2026", "6,120", "baseline".'),
    })
        .describe('The table view every chart carries (CONTRACT.md §2.13).'),
    series: z
        .array(z.object({
        label: z.string().describe('The month, short — "Aug".'),
        value: z.number().describe('The reading.'),
        baseline: z
            .boolean()
            .describe('True for a month from before the studio started, which is drawn muted.'),
    }))
        .describe('The points, oldest first. A month with no reading is absent, never zero.'),
    marker: z
        .object({
        at: z.int().describe('The index in series the rule is drawn at.'),
        label: z.string().describe('What the rule says — "work starts".'),
    })
        .nullable()
        .describe('The threshold rule report-2026-08.html §1 draws at the month work began, in the ' +
        'shape LineChart takes. Null when every month is a baseline, or none is.'),
})
    .describe('Chart data in the shape the portal’s Chart primitive takes. The axis ticks are the ' +
    'page’s: a scale is a rendering decision and Chart takes them as a prop.');
export const ClientMetricStatSchema = z
    .object({
    metric: OverviewMetricSchema,
    label: z.string().describe('What it is called — "Keywords in the top 10".'),
    short_label: z.string().describe('What the dashboard tile calls it — "Keywords in top 10".'),
    value: z.number().nullable().describe('The latest reading, or null when there is none.'),
    total: z
        .number()
        .nullable()
        .describe('The denominator when it reads "N of M" — 64. Null otherwise.'),
    fraction_digits: z.int().describe('Decimal places. Two for a ratio, none for a count.'),
    suffix: z.string().describe('What follows the figure — "×" for a ratio, empty otherwise.'),
    delta: ClientMetricDeltaSchema.nullable().describe('How it moved against the reading before it, or null when there is no reading before it.'),
    previous: ClientMetricReadingSchema.nullable().describe('The reading before, or null.'),
    baseline: ClientMetricReadingSchema.nullable().describe('The earliest reading from before the studio started — the "6,120 before we started". Null ' +
        'when there is no baseline month.'),
    note: z.string().describe('The line the report stored under it. Empty when there is none.'),
    breakdown: z
        .string()
        .describe('The detail the "Breakdown" fold prints. Empty when the figure has none.'),
    chart: ClientMetricChartSchema,
})
    .describe('One headline figure, as the dashboard prints it.');
export const ClientMetricsSchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema.nullable().describe('The month the figures come from, or null when none do.'),
    status: ReportStatusSchema.nullable().describe('Whether that report is issued or still in preparation. Null when there is no report.'),
    empty: z
        .string()
        .nullable()
        .describe('The line to print instead of the figures, or null when there are figures.'),
    stats: z
        .array(ClientMetricStatSchema)
        .describe('The four the dashboard prints, in its order, always four.'),
    breakdown: z
        .array(z.object({
        label: z.string().describe('Which figure — "Social reach".'),
        detail: z.string().describe('Its detail — "Instagram 68,200 · TikTok 47,900".'),
    }))
        .describe('The "Breakdown" fold’s lines: the figures that stored one, in dashboard order.'),
})
    .describe('The dashboard’s performance panel, from the latest report’s overview.');
//# sourceMappingURL=reports.js.map